import React, { useState, useEffect, useRef } from 'react';
import { 
  Bot, Send, Loader2, MessageSquare, X, Minimize2, Maximize2,
  Calendar, User, Eye, CheckCircle, XCircle, Clock, HelpCircle, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';

// Helper function to get icon for action type
const getActionIcon = (actionType) => {
  const iconMap = {
    'create_absence': Calendar,
    'request_time_off': Calendar,
    'show_employee': User,
    'employee_lookup': Search,
    'create_observation': Eye,
    'formal_observation': Eye,
    'quick_observation': Eye,
    'approve_request': CheckCircle,
    'deny_request': XCircle,
    'check_pending_requests': Clock,
    'help': HelpCircle,
  };
  return iconMap[actionType] || null;
};

// Helper function to get button style for action type
const getActionButtonStyle = (actionType) => {
  const styleMap = {
    'approve_request': 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
    'deny_request': 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
    'create_absence': 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
    'request_time_off': 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
    'create_observation': 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100',
  };
  return styleMap[actionType] || '';
};

const AIChatInterface = ({ isMinimized = false, onToggleMinimize, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions] = useState([
    "I need to take tomorrow off",
    "Find employee John Smith", 
    "Record a quick observation",
    "Show pending approvals"
  ]);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation history on mount
  useEffect(() => {
    loadConversationHistory();
  }, []);

  const loadConversationHistory = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getAIConversationHistory(null, 20);
      if (response.history && response.history.length > 0) {
        const formattedMessages = response.history.map(msg => ({
          id: msg.id || Date.now() + Math.random(),
          type: msg.role === 'user' ? 'user' : 'ai',
          content: msg.content,
          timestamp: new Date(msg.timestamp || Date.now()),
          actions: msg.actions || []
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Failed to load conversation history:', error);
      // Don't show error toast for history loading failure
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await apiService.sendAIChatMessage(
        message,
        sessionId ? { session_id: sessionId } : {}
      );

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.response,
        timestamp: new Date(),
        actions: response.actions || []
      };

      setMessages(prev => [...prev, aiMessage]);
      setSessionId(response.session_id);

    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: "I'm sorry, I encountered an error processing your request. Please try again or contact support.",
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      toast({
        title: 'Chat Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  const handleActionClick = async (action) => {
    try {
      setIsTyping(true);
      
      // Send the action as a follow-up message to the AI
      // This allows the AI to process the action and respond appropriately
      const actionMessage = action.label || action.type;
      
      // Add user action as a message
      const userMessage = {
        id: Date.now(),
        type: 'user',
        content: `[Action: ${actionMessage}]`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
      
      // Execute the action through the AI
      const response = await apiService.sendAIChatMessage(
        actionMessage,
        { 
          session_id: sessionId,
          action: action 
        }
      );
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.response,
        timestamp: new Date(),
        actions: response.actions || []
      };
      
      setMessages(prev => [...prev, aiMessage]);
      setSessionId(response.session_id);
      
      // Show success toast based on action type
      switch (action.type) {
        case 'create_absence':
          toast({
            title: 'Absence Request',
            description: 'Your time off request has been processed.'
          });
          break;
        case 'show_employee':
          toast({
            title: 'Employee Information',
            description: `Showing details for ${action.data?.name ?? 'employee'}`
          });
          break;
        case 'create_observation':
          toast({
            title: 'Observation Recorded',
            description: 'Your observation has been saved successfully.'
          });
          break;
        case 'approve_request':
          toast({
            title: 'Request Approved',
            description: 'The request has been approved.'
          });
          break;
        case 'deny_request':
          toast({
            title: 'Request Denied',
            description: 'The request has been denied.'
          });
          break;
        case 'check_pending_requests':
          toast({
            title: 'Pending Requests',
            description: 'Checking pending requests...'
          });
          break;
        default:
          // No toast for generic actions
          break;
      }
    } catch (error) {
      console.error('Action failed:', error);
      toast({
        title: 'Action Failed',
        description: 'Failed to complete the requested action.',
        variant: 'destructive'
      });
    } finally {
      setIsTyping(false);
    }
  };

  const clearConversation = async () => {
    try {
      await apiService.clearAIConversation(sessionId);
      setMessages([]);
      setSessionId(null);
      toast({
        title: 'Conversation Cleared',
        description: 'Chat history has been cleared.'
      });
    } catch (error) {
      console.error('Failed to clear conversation:', error);
      toast({
        title: 'Clear Failed',
        description: 'Failed to clear conversation history.',
        variant: 'destructive'
      });
    }
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={onToggleMinimize}
          className="w-14 h-14 rounded-full bg-yellow-500 hover:bg-yellow-600 text-black shadow-lg"
        >
          <MessageSquare className="w-6 h-6" />
        </Button>
      </div>
    );
  }

  return (
    <Card className="h-96 flex flex-col shadow-xl border-0 bg-white">
      <CardHeader className="pb-3 bg-gradient-to-r from-gray-900 to-black text-white rounded-t-lg">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-lg">
            <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
              <Bot className="w-5 h-5 text-black" />
            </div>
            PeoplePulse Assistant
          </CardTitle>
          <div className="flex items-center space-x-2">
            {onToggleMinimize && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleMinimize}
                className="text-white hover:bg-gray-800 h-8 w-8 p-0"
              >
                <Minimize2 className="w-4 h-4" />
              </Button>
            )}
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white hover:bg-gray-800 h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 p-0 overflow-hidden">
        <ScrollArea className="h-full p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading conversation...</span>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                <Bot className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Hi! I'm your PeoplePulse Assistant
              </h3>
              <p className="text-sm text-gray-500 mb-4 max-w-xs">
                I can help you with time off requests, employee information, observations, and more.
              </p>
              <div className="space-y-2 w-full max-w-xs">
                {suggestions.map((suggestion, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left justify-start text-xs"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <MessageList 
              messages={messages} 
              isTyping={isTyping} 
              onActionClick={handleActionClick}
            />
          )}
          <div ref={messagesEndRef} />
        </ScrollArea>
      </CardContent>

      <CardFooter className="p-4 border-t">
        <form onSubmit={handleSubmit} className="flex w-full space-x-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Ask me about time off, employee info, or observations..."
            disabled={isTyping}
            className="flex-1"
          />
          <Button 
            type="submit" 
            disabled={isTyping || !inputValue.trim()}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            {isTyping ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
        {messages.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearConversation}
            className="ml-2 text-xs text-gray-500 hover:text-gray-700"
          >
            Clear
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

const MessageList = ({ messages, isTyping, onActionClick }) => {
  return (
    <div className="space-y-4">
      {messages.map((message) => (
        <MessageBubble 
          key={message.id} 
          message={message} 
          onActionClick={onActionClick}
        />
      ))}
      {isTyping && <TypingIndicator />}
    </div>
  );
};

const MessageBubble = ({ message, onActionClick }) => {
  const isUser = message.type === 'user';
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
        isUser 
          ? 'bg-yellow-500 text-black' 
          : message.isError 
            ? 'bg-red-50 text-red-800 border border-red-200'
            : 'bg-gray-100 text-gray-900'
      }`}>
        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        
        {message.actions && message.actions.length > 0 && (
          <div className="mt-3 space-y-2">
            {message.actions.map((action, index) => {
              const ActionIcon = getActionIcon(action.type);
              const buttonStyle = getActionButtonStyle(action.type);
              
              return (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => onActionClick(action)}
                  className={`w-full text-left justify-start text-xs ${buttonStyle}`}
                >
                  {ActionIcon && <ActionIcon className="w-3 h-3 mr-2" />}
                  {action.label || action.type}
                </Button>
              );
            })}
          </div>
        )}
        
        <div className="mt-1">
          <span className="text-xs opacity-70">
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>
    </div>
  );
};

const TypingIndicator = () => {
  return (
    <div className="flex justify-start">
      <div className="bg-gray-100 px-4 py-2 rounded-lg">
        <div className="flex items-center space-x-1">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
          <span className="text-xs text-gray-500 ml-2">Assistant is typing...</span>
        </div>
      </div>
    </div>
  );
};

export default AIChatInterface;